import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ApproveAnimatedButton } from './ApproveAnimatedButton';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REPORTED' | 'EXPIRED';

export interface RequestDetails {
  store: string;
  requestedAt: string;
  validTill: string;
}

interface PendingScreenProps {
  details: RequestDetails;
  onAction: (status: ApprovalStatus) => Promise<unknown>;
}

export function PendingScreen({ details, onAction }: PendingScreenProps) {
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  return (
    <div className="flex flex-col gap-10 flex-1 animate-in fade-in duration-500">
      <p className="text-[#314158] text-[16px] px-2 text-center mb-6 tracking-wider leading-relaxed">
        We received a request to use your phone number for login at a Bluestone store.
        <br /><br />
        Please confirm whether this request was made by you.
      </p>

      {/* Details Card */}
      <div className="bg-[#F8FAFC] flex flex-col gap-3 rounded-[14px] border border-[#E2E8F0] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <span className="text-sm font-semibold tracking-widest text-[#0F172B] uppercase">
          Request Details
        </span>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-[#45556C]">Store:</span>
            <span className="font-medium text-[#0F172B]">{details.store}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#45556C]">Requested at:</span>
            <span className="font-medium text-[#0F172B]">{details.requestedAt}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#45556C]">Valid till:</span>
            <span className="font-medium text-[#0F172B]">{details.validTill}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col space-y-4 mb-8">
        <ApproveAnimatedButton onClick={() => onAction('APPROVED')} />

        <button
          onClick={async () => {
            setIsRejecting(true);
            await onAction('REJECTED');
          }}
          disabled={isRejecting}
          className="w-full bg-white text-[#011E37] text-sm tracking-wider border border-[#B6B6B6] py-4 rounded-xl font-semibold shadow-[0px_4px_4px_0px_#00000040] transition-all active:scale-[0.98] disabled:opacity-70 flex justify-start pl-6 items-center"
        >
          {isRejecting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'REJECT'}
        </button>

        <button
          onClick={async () => {
            setIsReporting(true);
            await onAction('REPORTED');
          }}
          disabled={isReporting}
          className="w-full bg-white text-[#C82020] text-sm tracking-wider border border-[#B6B6B6] py-4 rounded-xl font-semibold shadow-[0px_4px_4px_0px_#00000040] transition-all active:scale-[0.98] disabled:opacity-70 flex justify-start pl-6 items-center "
        >
          {isReporting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'REPORT MISUSE'}
        </button>
        <p className="text-center text-xs text-[#62748E] tracking-widest mb-8 pt-2">
            If you did not request this login, please reject or report misuse.
        </p>
      </div>
    </div>
  );
}